import { Button as ButtonPrimitive } from '@base-ui/react'
import { cva, type VariantProps } from 'class-variance-authority'
import type { LucideIcon } from 'lucide-react'
import type { ButtonHTMLAttributes } from 'react'

import { cn } from '@/lib/cn'

/* Buttons are 1–3 words, verb-first, no punctuation, sentence case:
   "New quote", "Record payment", "Reconcile". Never Title Case. */

const button = cva(
  'inline-flex items-center justify-center gap-2 rounded-md font-sans whitespace-nowrap ' +
    'transition-[background-color,border-color,color,box-shadow] duration-[var(--dur-fast)] ' +
    'ease-[var(--ease-standard)] cursor-pointer ' +
    'disabled:cursor-not-allowed disabled:opacity-55',
  {
    variants: {
      variant: {
        // Gateway Blue is the single accent and must stay the brightest thing
        // on screen. It carries identity and the primary action only.
        primary:
          'bg-brand text-text-on-brand border border-brand hover:bg-blue-600 hover:border-blue-600',
        secondary:
          'bg-surface text-text-body border border-border-default hover:bg-surface-hover hover:text-text-heading',
        ghost:
          'bg-transparent text-text-muted border border-transparent hover:bg-surface-hover hover:text-text-heading',
        // Destructive and financial actions ALWAYS carry a text label; an icon
        // alone is never enough.
        danger:
          'bg-surface text-negative border border-negative/35 hover:bg-negative-soft',
      },
      /* Heights are the DESKTOP measure. On a coarse pointer every size grows
         to the 44px touch minimum (UX-DR-27) — a 30px button is comfortable
         with a mouse and a miss with a thumb, and this team works from phones
         at border posts.

         `pointer-coarse:` rather than a width breakpoint: the input device is
         the thing that matters, and a tablet with a keyboard and trackpad
         should keep the tighter desktop rhythm. */
      size: {
        sm: 'h-[30px] pointer-coarse:h-11 px-3 text-[12.5px] font-normal',
        md: 'h-9 pointer-coarse:h-11 px-3.5 text-[13px] font-normal',
        lg: 'h-10 pointer-coarse:h-11 px-4 text-[14px] font-normal',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

const ICON_SIZE = { sm: 14, md: 16, lg: 16 } as const

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  iconLeft?: LucideIcon
  iconRight?: LucideIcon
  /** Why this button is refusing — a business rule the reader can act on, not a
   *  transient pending state. Supplying it turns the button off AND says so.
   *
   *  Use this instead of `disabled` whenever a sentence exists. A `disabled`
   *  button is a dead end: it cannot be hovered, cannot be focused and is
   *  skipped by a screen reader, so the one reader who most needs the reason is
   *  the one who cannot reach it. Reserve bare `disabled` for states whose
   *  cause is already on screen — a mutation in flight, where the button's own
   *  label has changed to "Sending…". */
  blockedReason?: string
}

export function Button({
  className,
  variant,
  size = 'md',
  iconLeft: IconLeft,
  iconRight: IconRight,
  children,
  type = 'button',
  blockedReason,
  disabled,
  onClick,
  title,
  ...props
}: ButtonProps) {
  const iconSize = ICON_SIZE[size ?? 'md']
  /* Two different refusals, and Base UI expresses both through one pair of
     props. `disabled` alone is the browser's: the native attribute, no hover,
     no tab stop — right only when the reason is already on screen.
     `focusableWhenDisabled` swaps that for `aria-disabled="true"` with the tab
     stop and the hover kept, and suppresses the activation itself, which is
     exactly the hand-rolled preventDefault/stopPropagation guard this replaces.

     `blocked && !disabled` rather than `blocked`: a call site that passes BOTH
     is asking for the native disabled, and promoting it to focusable would
     quietly re-enable a control the caller turned off. */
  const blocked = Boolean(blockedReason)
  return (
    <ButtonPrimitive
      type={type}
      disabled={disabled || blocked}
      focusableWhenDisabled={blocked && !disabled}
      title={blockedReason ?? title}
      /* The primitive's own refusal only calls preventDefault, so a blocked
         press would still bubble to a clickable ancestor — a row that opens a
         drawer, say. Capture phase rather than `onClick`, because the primitive
         swallows `onClick` while disabled and this handler would never run;
         preventDefault is repeated here since stopping propagation also stops
         the primitive's handler from getting the event. */
      onClickCapture={
        blocked
          ? (event) => {
              event.preventDefault()
              event.stopPropagation()
            }
          : undefined
      }
      onClick={onClick}
      className={cn(
        button({ variant, size }),
        // The `disabled:` variants cannot see aria-disabled, so the blocked
        // look is spelled out rather than left to diverge from the real thing.
        blocked && 'cursor-not-allowed opacity-55',
        className,
      )}
      {...props}
    >
      {IconLeft ? <IconLeft size={iconSize} aria-hidden /> : null}
      {children}
      {IconRight ? <IconRight size={iconSize} aria-hidden /> : null}
    </ButtonPrimitive>
  )
}
